import { useState, useEffect } from "react";
import { MapPin, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { getCurrentPositionSafe, isGeolocationSupported } from "../lib/geolocation";

/**
 * LocationConsentBlock — renders on public pages that have opted in to
 * geo-fenced brand attribution. Asks the visitor for browser geolocation
 * permission, posts the result (lat/lng or `granted=false`) to the backend
 * where it is resolved to the nearest Location Master node and persisted
 * against the visitor_id. Downstream, the brand's attribution engine uses
 * this to decide whether the visitor is "in-target" for their campaigns.
 *
 * Visitor UX contract:
 *  - Idle → shows the "Share my location" CTA.
 *  - Prompting → browser dialog is open.
 *  - Granted → shows the resolved city name (e.g. "Nellore, India").
 *  - Denied → non-blocking, still lets the visitor use the rest of the page.
 *
 * The visitor_id is a lightweight cookie already stamped by the visit
 * endpoint; we forward the same value.
 */
export function LocationConsentBlock({ block, editable, onClick: _onClick }) {
  const c = block.config || {};
  const [status, setStatus] = useState("idle"); // idle | prompting | granted | denied | error
  const [location, setLocation] = useState(null); // { name }

  // Read the same visitor cookie the visit tracker stamps so the geo consent
  // links back to the correct visit record on the server.
  const readVisitorId = () => {
    const m = document.cookie.match(/(?:^|;\s*)ah_vid=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  };

  const submit = async ({ lat, lng, granted }) => {
    const parts = window.location.pathname.split("/").filter(Boolean);
    if (parts[0] !== "u" || parts.length < 2) return;
    const creator_slug = parts[1];
    const page_slug = parts[2] || null;
    const url = page_slug
      ? `${process.env.REACT_APP_BACKEND_URL}/api/public/pages/${creator_slug}/${page_slug}/location`
      : `${process.env.REACT_APP_BACKEND_URL}/api/public/creators/${creator_slug}/location`;
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          visitor_id: readVisitorId(),
          lat, lng, granted,
        }),
      });
      const data = await res.json();
      return data;
    } catch { return null; }
  };

  const askPermission = () => {
    if (editable) return;
    if (!isGeolocationSupported()) {
      setStatus("error");
      submit({ granted: false });
      return;
    }
    setStatus("prompting");
    getCurrentPositionSafe(
      async (pos) => {
        const data = await submit({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          granted: true,
        });
        setLocation(data && data.location_name ? { name: data.location_name } : null);
        setStatus("granted");
      },
      async () => {
        await submit({ granted: false });
        setStatus("denied");
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 },
    );
  };

  // Auto-prompt on mount when `required=true` (creator wants a hard gate).
  useEffect(() => {
    if (editable) return;
    if (c.required && status === "idle") {
      const t = setTimeout(askPermission, 500);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.required, editable]);

  return (
    <div
      className="ah-block border-2 border-ink bg-cream p-4 text-center space-y-3"
      data-testid={`block-location-consent-${block.block_id}`}
    >
      <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider opacity-70">
        <MapPin className="w-3.5 h-3.5" /> Location
      </div>
      <h4 className="font-display font-black text-lg tracking-tighter">
        {c.headline || "Show local offers"}
      </h4>
      {c.subtext && <p className="text-sm opacity-80">{c.subtext}</p>}

      {status === "idle" && (
        <button
          onClick={askPermission}
          disabled={editable}
          className="border-2 border-ink bg-coral text-white px-4 py-2 font-bold text-sm shadow-brutalSm inline-flex items-center gap-2"
          data-testid={`block-location-consent-cta-${block.block_id}`}
        >
          <MapPin className="w-4 h-4" /> {c.cta_label || "Share my location"}
        </button>
      )}
      {status === "prompting" && (
        <div className="inline-flex items-center gap-2 text-sm font-bold" data-testid={`block-location-consent-pending-${block.block_id}`}>
          <Loader2 className="w-4 h-4 animate-spin" /> Waiting for permission…
        </div>
      )}
      {status === "granted" && (
        <div className="inline-flex items-center gap-2 text-sm font-bold text-green-700" data-testid={`block-location-consent-granted-${block.block_id}`}>
          <CheckCircle2 className="w-4 h-4" />
          {location?.name ? `Detected: ${location.name}` : "Location received"}
        </div>
      )}
      {status === "denied" && (
        <div className="text-xs opacity-70" data-testid={`block-location-consent-denied-${block.block_id}`}>
          <XCircle className="w-3.5 h-3.5 inline mr-1" />
          Location not shared — you can still browse everything on this page.
        </div>
      )}
      {status === "error" && (
        <p className="text-xs opacity-70">Your browser doesn&apos;t support location sharing.</p>
      )}
    </div>
  );
}
